import isEmpty from 'lodash/isEmpty';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import {
  createUserWithIdp,
  createUserWithOpenIdpApi,
  getSellerAppUserApi,
  loginUserWithOpenIdp,
  sellerUserExist,
  syncGuestCheckoutPromotixUser,
} from '../util/api';
import { storableError } from '../util/errors';
import { resetLocalCheckoutConfig } from '../util/localCheckout';
import { resetLocalPaymentsConfig } from '../util/localPayments';
import * as log from '../util/log';
import {
  createGuestCheckoutAttempt,
  clearGuestCheckoutAttempt,
  runGuestCheckoutAttempt,
} from '../util/guestCheckoutAttempt';

const authenticated = authInfo => authInfo && authInfo.isAnonymous === false;

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const GUEST_CHECKOUT_REQUEST = 'app/auth/GUEST_CHECKOUT_REQUEST';
export const GUEST_CHECKOUT_END = 'app/auth/GUEST_CHECKOUT_END';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';


export const CONFIRM_SSO_REQUEST = 'app/Auth/CONFIRM_SSO_REQUEST';
export const CONFIRM_SSO_SUCCESS = 'app/Auth/CONFIRM_SSO_SUCCESS';
export const CONFIRM_SSO_ERROR = 'app/Auth/CONFIRM_SSO_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,
  guestCheckoutInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [] };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case GUEST_CHECKOUT_REQUEST:
      return { ...state, guestCheckoutInProgress: true };
    case GUEST_CHECKOUT_END:
      return { ...state, guestCheckoutInProgress: false };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

      case CONFIRM_SSO_REQUEST:
        return { ...state, confirmSSOInProgress: true, confirmSSOError: null };
      case CONFIRM_SSO_SUCCESS:
        return { ...state, confirmSSOInProgress: false, isAuthenticated: true };
      case CONFIRM_SSO_ERROR:
        return { ...state, confirmSSOInProgress: false, confirmSSOError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress, guestCheckoutInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress || !!guestCheckoutInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });


export const confirmSSORequest = () => ({ type: CONFIRM_SSO_REQUEST });
export const confirmSSOSuccess = () => ({ type: CONFIRM_SSO_SUCCESS });
export const confirmSSOError = error => ({ type: CONFIRM_SSO_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return sdk
    .login({ username, password })
    .then(() => dispatch(loginSuccess()))
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      resetLocalPaymentsConfig();
      resetLocalCheckoutConfig();
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  const { email, password, firstName, lastName, ...rest } = params;

  const createUserParams = isEmpty(rest)
    ? { email, password, firstName, lastName }
    : { email, password, firstName, lastName, protectedData: { ...rest } };

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  return sdk.currentUser
    .create(createUserParams)
    .then(() => dispatch(signupSuccess()))
    .then(() => dispatch(login(email, password)))
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
      });
    });
};

/**
 * Guest checkout signup — creates a Flex user, logs in, immediately syncs the
 * user to PromoTix as inactive, and returns a safe stage/reason result so
 * the checkout flow can decide whether to proceed with the privileged
 * `request-payment` transition.
 *
 * Why this exists: Sharetribe Flex requires an authenticated `actor.role/customer`
 * for privileged `request-payment`. Anonymous (public-read) tokens cannot run
 * customer transitions even via the trusted SDK. Guest checkout therefore must
 * create a user first. We intentionally do NOT dispatch fetchCurrentUser here:
 * doing so flips CheckoutPage from the guest render to the logged-in render while
 * Stripe is still using the submitted Card Element, which can unmount the Element
 * and leave the transaction stuck at request-payment. TransactionPage.loadData
 * hydrates the user after checkout navigates to the order.
 * The server-side PromoTix sync uses the fresh login cookie instead; the user/created
 * poller remains as a retry/backstop.
 */
export const signupGuestForCheckout = (params, attempt) => async (dispatch, getState, sdk) => {
  // Legacy callers get a one-shot token; CheckoutPage supplies a mounted token so
  // retries retain the original generated password after partial success.
  const guestAttempt = attempt || createGuestCheckoutAttempt();
  try {
    const result = await runGuestCheckoutAttempt(guestAttempt, params, {
      authenticationInProgress: () => {
        const { loginInProgress, logoutInProgress, signupInProgress } = getState().auth;
        return loginInProgress || logoutInProgress || signupInProgress;
      },
      onFlowStart: () => dispatch({ type: GUEST_CHECKOUT_REQUEST }),
      onFlowEnd: () => dispatch({ type: GUEST_CHECKOUT_END }),
      createUser: fields => sdk.currentUser.create(fields),
      login: credentials => sdk.login(credentials),
      // Deliberately do not dispatch fetchCurrentUser: keep the Stripe Element mounted.
      showCurrentUser: () => sdk.currentUser.show(),
      readAuthInfo: () => sdk.authInfo(),
      syncPromotix: () => syncGuestCheckoutPromotixUser({}),
      onStage: (stage, status, failure) => {
        if (stage === 'create') {
          dispatch(
            status === 'start'
              ? signupRequest()
              : status === 'success'
              ? signupSuccess()
              : signupError(storableError(failure.error))
          );
        } else if (stage === 'login') {
          dispatch(
            status === 'start'
              ? loginRequest()
              : status === 'success'
              ? loginSuccess()
              : loginError(storableError(failure.error))
          );
        }
      },
    });
    if (!result.ok) {
      log.error(result.error, 'guest-checkout-step-failed', {
        stage: result.stage,
        reason: result.reason,
      });
    }
    return result;
  } finally {
    if (!attempt) clearGuestCheckoutAttempt(guestAttempt);
  }
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  return createUserWithIdp(params)
    .then(res => {
      return dispatch(confirmSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};


export const loginWithSellerApp = params => async (dispatch, getState, sdk) => {
  const { auth_token } = params;

  try {
    dispatch(confirmSSORequest());

    const sellerResponse = await getSellerAppUserApi({ auth_token });
    if (sellerResponse?.missingAuthToken) {
      dispatch(confirmSSOError(null));
      return null;
    }
    const inner =
      sellerResponse &&
      sellerResponse.userDetails &&
      typeof sellerResponse.userDetails === 'object'
        ? sellerResponse.userDetails
        : sellerResponse;
    const userDetails = inner && typeof inner === 'object' ? inner : {};
    const authPayload = { userDetails };

    const userExists = await sellerUserExist(authPayload);
    if (userExists?.user && userExists.user?.id) {
      const response = await loginUserWithOpenIdp(authPayload);
      dispatch(confirmSSOSuccess());
      dispatch(fetchCurrentUser());
      return response;
    }
    const response = await createUserWithOpenIdpApi(authPayload);
    dispatch(confirmSSOSuccess());
    dispatch(fetchCurrentUser());
    return response;
  } catch (error) {
    log.error(error, 'create-user-with-idp-failed', { params, error });
    const storedError = {
      ...storableError(error),
      code: error?.code,
      data: error?.data,
      statusText: error?.statusText,
    };
    return dispatch(confirmSSOError(storedError));
  }
};